import { ContentAction } from "@amzn/ads-blink-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import type { DropdownItemData } from "../../components/common/DropdownHelpers";
import * as DropdownDimensionHelpers from "../../components/dimensions/DropdownDimensionHelpers";
import { NO_ACTIVITY_DROPDOWN_ITEM } from "../../utils/salesforce.utils";
import { useAutoFetch } from "../misc/useAutoFetch";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

export const emptyDropdownItems: DropdownItemData[] = [
    NO_ACTIVITY_DROPDOWN_ITEM,
];

export interface SfActivityActionProps {
    advertiserId: string;
    startDate: string;
    endDate: string;
    contentAction?: ContentAction;
    autoFetch?: boolean;
}

export function useGetSfActivities({
    advertiserId,
    startDate,
    endDate,
    contentAction,
    autoFetch = false,
}: SfActivityActionProps): [
    AsyncState<DropdownItemData[]>,
    UseAsyncActions<DropdownItemData[], []>,
] {
    const [state, action] = useAsync(async () => {
        const client = await getCdbApiClient();
        const response = await client.getSfActivities({
            advertiserId,
            startDate,
            endDate,
        });

        const data = response.data ?? [];
        if (data.length === 0) {
            return [NO_ACTIVITY_DROPDOWN_ITEM];
        }

        const parsedData = DropdownDimensionHelpers.processItemMapper(
            data,
            DropdownDimensionHelpers.defaultRequestParameterMapping("activity"),
        );

        return contentAction === ContentAction.UPLOAD
            ? [...parsedData, NO_ACTIVITY_DROPDOWN_ITEM]
            : [NO_ACTIVITY_DROPDOWN_ITEM, ...parsedData];
    }, emptyDropdownItems);

    useAutoFetch(action, autoFetch);
    useAsyncErrorStatusNotification(state);

    return [state, action];
}
